'use client'

import * as React from 'react'
import { AnimatedSpinnerIcon } from 'primitives/icons/animated-spinner-icon'
import type { StartFreeTrialProps } from './hooks/use-start-free-trial'
import { useStartFreeTrial } from './hooks/use-start-free-trial'
import type { PAYMENT_ENTRY_POINTS } from '../payments-dialog/analytics'
import { savePaymentModalEntryPoint } from '../payments-dialog/analytics'
import { tw } from 'utils/classnames'
import { useIsLoggedInQuery } from 'auth/hooks'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import { defaultLanguage } from 'cf-i18n/constants'

type StartFreeTrialButtonProps = StartFreeTrialProps & {
  style?: React.CSSProperties
  entryPoint: PAYMENT_ENTRY_POINTS
  label: string | React.ReactNode
  className?: string
  onClick?: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void
  enableKeyboardSubmit?: boolean
}

export function StartFreeTrialButton({
  entryPoint,
  label,
  className,
  onClick,
  onKeyDown,
  redirectUrl,
  style = {},
  enableKeyboardSubmit = false,
  ...hookProps
}: StartFreeTrialButtonProps) {
  const locale = useSafeLocale()
  const isLoggedInQuery = useIsLoggedInQuery()

  const { startFreeTrial, isPending } = useStartFreeTrial({ redirectUrl, ...hookProps })

  const handleFreeTrialClick = React.useCallback(async () => {
    if (isPending) {
      return
    }

    savePaymentModalEntryPoint(entryPoint)

    if (onClick) {
      onClick()
    }

    await startFreeTrial()
  }, [startFreeTrial, entryPoint, onClick, isPending])

  const handleKeyboardSubmit = React.useCallback(
    async (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (isPending) {
        return
      }

      if (enableKeyboardSubmit) {
        e.preventDefault()
        savePaymentModalEntryPoint(entryPoint)

        if (onKeyDown) {
          onKeyDown(e)
        }

        await startFreeTrial()
      }
    },
    [startFreeTrial, entryPoint, onKeyDown, enableKeyboardSubmit, isPending],
  )

  let redirectUrlFinal =
    hookProps.subscriptionType === 'studio-ai' ? '/b/free-trial-aistudio/' : '/b/free-trial/'

  if (redirectUrl) {
    redirectUrlFinal = redirectUrl
  }

  redirectUrlFinal = addOrReplaceLocaleInUrl(redirectUrlFinal, locale)

  if (!isLoggedInQuery.data && redirectUrlFinal.length > 0) {
    return (
      <a
        onClick={onClick}
        style={style}
        href={redirectUrlFinal}
        className={tw('relative flex items-center justify-center', className)}
        {...(typeof label === 'string' ? { dangerouslySetInnerHTML: { __html: label } } : {})}
      >
        {typeof label !== 'string' ? label : null}
      </a>
    )
  }

  return (
    <button
      style={style}
      onClick={handleFreeTrialClick}
      onKeyDown={handleKeyboardSubmit}
      disabled={isPending}
      className={tw('relative flex items-center justify-center', className)}
    >
      <div
        className={isPending ? 'invisible' : 'visible'}
        {...(typeof label === 'string' ? { dangerouslySetInnerHTML: { __html: label } } : {})}
      >
        {typeof label !== 'string' ? label : null}
      </div>
      {isPending ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <AnimatedSpinnerIcon />
        </span>
      ) : null}
    </button>
  )
}

function useSafeLocale() {
  if (typeof document === 'undefined') {
    return defaultLanguage
  }

  return document.documentElement.lang || defaultLanguage
}
